/**
 * Installation Jobs API Functions
 */

import { api } from '@/lib/api';
import type {
  InstallationJob,
  InstallationJobTemplate,
  JobsResponse,
  JobsQueryParams,
  TemplatesResponse,
  CreateJobInput,
  UpdateJobInput,
  GenerateCodeResponse,
  RejectInstallationJobResponse,
  AuditLogsResponse,
  DispatchToFieldNationResponse,
  SyncFieldNationResponse,
} from './types';

// =============================================================================
// Installation Jobs
// =============================================================================

export async function fetchInstallationJobs(params: JobsQueryParams = {}): Promise<JobsResponse> {
  return api.get<JobsResponse>('/v4/installation-jobs', params);
}

export async function fetchInstallationJob(id: string): Promise<InstallationJob> {
  return api.get<InstallationJob>(`/v4/installation-jobs/${id}`);
}

export async function createInstallationJob(input: CreateJobInput): Promise<InstallationJob> {
  return api.post<InstallationJob>('/v4/installation-jobs', input);
}

export async function updateInstallationJob(id: string, input: UpdateJobInput): Promise<InstallationJob> {
  return api.put<InstallationJob>(`/v4/installation-jobs/${id}`, input);
}

export async function markInstallationIqCreated(
  jobId: string,
  sensorId: string,
  input: {
    createdAt: string;
    createdReportId: string;
    createdReportNumber: string;
  },
): Promise<InstallationJob> {
  return api.patch<InstallationJob>(
    `/v4/installation-jobs/${jobId}/sensors/${sensorId}/iq-draft`,
    { status: 'created', ...input },
  );
}

export async function deleteInstallationJob(id: string): Promise<{ deleted: boolean; id: string; jobNumber: string }> {
  return api.delete(`/v4/installation-jobs/${id}`);
}

export async function rejectInstallationJob(id: string, input: { reason: string; notifyEmail?: string }): Promise<RejectInstallationJobResponse> {
  return api.post<RejectInstallationJobResponse>(`/v4/installation-jobs/${id}/reject`, input);
}

// =============================================================================
// Access Codes
// =============================================================================

export async function generateAccessCode(
  jobId: string,
  expiresInDays: number = 7
): Promise<GenerateCodeResponse> {
  return api.post<GenerateCodeResponse>(`/v4/installation-jobs/${jobId}/access-codes`, {
    expiresInDays,
  });
}

export async function revokeAccessCode(
  jobId: string,
  codeHash: string
): Promise<{ revoked: boolean }> {
  return api.delete(`/v4/installation-jobs/${jobId}/access-codes/${codeHash}`);
}

export async function sendAccessCode(
  jobId: string,
  codeId: string,
  method: 'email' | 'sms',
  recipient: string,
  code?: string,
  language?: 'en' | 'fr'
): Promise<{ sent: boolean; emailSent?: boolean }> {
  return api.post(`/v4/installation-jobs/${jobId}/access-codes/${codeId}/send`, {
    method,
    recipient,
    code,
    language,
  });
}

// =============================================================================
// Site Sensors
// =============================================================================

/** Fetch MongoDB sensors for the job's site */
export async function fetchSiteSensors(jobId: string): Promise<{ sensors: Array<{ _id: string; name: string }> }> {
  return api.get<{ sensors: Array<{ _id: string; name: string }> }>(`/v4/installation-jobs/${jobId}/site-sensors`);
}

/** Fetch sensors by siteId (used before job exists) */
export async function fetchSensorsBySite(siteId: string): Promise<{ sensors: Array<{ _id: string; name: string }>; count: number }> {
  return api.get<{ sensors: Array<{ _id: string; name: string }>; count: number }>(`/v4/installation-jobs/sensors-by-site`, { siteId });
}

/**
 * Fetch current legacy sensors for an account through the V4 read-through route.
 * This keeps newly created Mongo sensors selectable before account materialization
 * assigns them a PostgreSQL account_id.
 */
export interface AvailableAccountSensor {
  _id: string;
  name: string;
  stream?: { name: string };
  location?: { name: string };
}

export async function fetchAvailableAccountSensors(accountId: string): Promise<AvailableAccountSensor[]> {
  const response = await api.get<{
    sensors: Array<{ _id: string; name: string; streamName: string | null }>;
  }>(`/v4/accounts/${accountId}/importable-sensors`);

  return (response.sensors || []).map((sensor) => ({
    _id: sensor._id,
    name: sensor.name,
    ...(sensor.streamName ? { stream: { name: sensor.streamName } } : {}),
  }));
}

// =============================================================================
// Audit Logs
// =============================================================================

export async function fetchAuditLogs(
  jobId: string,
  params: { pageNumber?: number; pageLength?: number } = {}
): Promise<AuditLogsResponse> {
  return api.get<AuditLogsResponse>(`/v4/installation-jobs/${jobId}/audit-logs`, params);
}

// =============================================================================
// Templates
// =============================================================================

export async function fetchTemplates(): Promise<TemplatesResponse> {
  return api.get<TemplatesResponse>('/v4/installation-job-templates');
}

export async function fetchTemplate(id: string): Promise<InstallationJobTemplate> {
  return api.get<InstallationJobTemplate>(`/v4/installation-job-templates/${id}`);
}

export async function createTemplate(
  input: Omit<InstallationJobTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
): Promise<InstallationJobTemplate> {
  return api.post<InstallationJobTemplate>('/v4/installation-job-templates', input);
}

export async function updateTemplate(
  id: string,
  input: Partial<Omit<InstallationJobTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>
): Promise<InstallationJobTemplate> {
  return api.put<InstallationJobTemplate>(`/v4/installation-job-templates/${id}`, input);
}

export async function deleteTemplate(id: string): Promise<{ deleted: boolean; id: string; name: string }> {
  return api.delete(`/v4/installation-job-templates/${id}`);
}

// =============================================================================
// File URLs (Photos/Signatures)
// =============================================================================

/**
 * Get a signed URL for a checklist file (photo or signature)
 * Returns a GCS signed URL that expires in 1 hour
 */
export async function getChecklistFileUrl(jobId: string, fileId: string): Promise<string> {
  const response = await api.get<{ url: string }>(`/v4/installation-jobs/${jobId}/files/${fileId}`);
  return response.url;
}

export async function getChecklistFileContent(
  jobId: string,
  fileId: string,
): Promise<{ data: string; contentType: string }> {
  return api.get<{ data: string; contentType: string }>(
    `/v4/installation-jobs/${jobId}/files/${fileId}`,
    { content: 'base64' },
  );
}

// =============================================================================
// Field Nation Integration
// =============================================================================

/**
 * Dispatch an installation job to Field Nation
 * Creates a work order in Field Nation and links it to this job
 */
export async function dispatchToFieldNation(jobId: string): Promise<DispatchToFieldNationResponse> {
  return api.post<DispatchToFieldNationResponse>(
    `/v4/installation-jobs/${jobId}/dispatch-fieldnation`,
    {}
  );
}

/**
 * Sync Field Nation status for a job
 * Fetches the current status from Field Nation and updates the local record
 */
export async function syncFieldNationStatus(jobId: string): Promise<SyncFieldNationResponse> {
  return api.post<SyncFieldNationResponse>(
    `/v4/installation-jobs/${jobId}/sync-fieldnation`,
    {}
  );
}
